"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const categories = [{
  id: "all",
  label: "Todo"
}, {
  id: "procedimiento",
  label: "Procedimiento"
}, {
  id: "recuperacion",
  label: "Recuperación"
}, {
  id: "resultados",
  label: "Resultados"
}, {
  id: "financiero",
  label: "Financiero"
}];
const faqs = [{
  id: 1,
  category: "candidatura",
  question: "¿Soy apto para un trasplante capilar?",
  answer: "La idoneidad depende de la calidad y densidad de tu zona donante, así como de la causa de tu alopecia. En MS Instituto Capilar, realizamos un diagnóstico tricológico exhaustivo para determinar si el trasplante es la mejor opción o si la medicina capilar puede ofrecerte resultados superiores.",
  callout: "Cada caso es único. Evaluamos tu salud capilar de forma personalizada en consulta."
}, {
  id: 2,
  category: "procedimiento",
  question: "¿Es dolorosa la intervención?",
  answer: "Gracias a nuestras técnicas de anestesia local avanzada, el procedimiento es prácticamente indoloro. La mayoría de nuestros pacientes descansan, escuchan música o ven una película durante la intervención, que se realiza en un entorno de total confort."
}, {
  id: 3,
  category: "procedimiento",
  question: "¿Cuánto tiempo dura la cirugía?",
  answer: "La duración varía según el número de unidades foliculares necesarias, oscilando generalmente entre 6 y 8 horas. Es un proceso meticuloso donde priorizamos la precisión y la supervivencia de cada folículo sobre la rapidez."
}, {
  id: 4,
  category: "recuperacion",
  question: "¿Cómo es el proceso de recuperación?",
  answer: "La recuperación es rápida y sencilla. Durante los primeros días, es normal una ligera inflamación que desaparece pronto. Proporcionamos un protocolo postoperatorio detallado para asegurar que el proceso sea cómodo y los resultados óptimos."
}, {
  id: 5,
  category: "resultados",
  question: "¿Cuándo veré los resultados definitivos?",
  answer: "El crecimiento del cabello es gradual. A partir del tercer o cuarto mes comenzarás a ver los primeros cambios, siendo el resultado final visible entre los 12 y 18 meses tras la intervención. La paciencia es clave en este proceso natural."
}, {
  id: 6,
  category: "procedimiento",
  question: "¿Qué técnica de trasplante utilizan?",
  answer: "Utilizamos la técnica FUE (Follicular Unit Extraction) de alta precisión, que permite extraer folículos de forma individual sin dejar cicatrices lineales visibles. Esto garantiza un resultado natural y una recuperación mucho más discreta."
}, {
  id: 7,
  category: "recuperacion",
  question: "¿Cuándo puedo volver a mi vida normal?",
  answer: "La mayoría de nuestros pacientes retoman sus actividades cotidianas en pocos días. Recomendamos evitar el ejercicio físico intenso y la exposición solar directa durante las primeras semanas para proteger la zona tratada.",
  callout: "Te acompañamos en cada paso con un seguimiento personalizado tras la cirugía."
}, {
  id: 8,
  category: "candidatura",
  question: "¿Existe una edad mínima para operarse?",
  answer: "No hay una edad mínima estricta, pero es fundamental que la alopecia se haya estabilizado. En pacientes jóvenes, solemos combinar el tratamiento médico para controlar la caída antes de considerar la cirugía."
}, {
  id: 9,
  category: "financiero",
  question: "¿Cómo funciona el presupuesto de la cirugía?",
  answer: "El presupuesto se personaliza tras el diagnóstico médico, ya que depende de la complejidad y el número de folículos necesarios. Ofrecemos opciones de financiación flexibles para que la salud capilar sea accesible."
}, {
  id: 10,
  category: "resultados",
  question: "¿El resultado se ve natural?",
  answer: "Nuestra filosofía es la naturalidad absoluta. Diseñamos la línea frontal respetando tus rasgos faciales y la angulación natural de tu cabello, logrando un resultado indetectable que se integra perfectamente con tu imagen."
}];
export default function PreguntasFrecuentesSobreCirugA() {
  const [activeCategory, setActiveCategory] = useState("all");
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const filteredFaqs = activeCategory === "all" ? faqs : faqs.filter(f => f.category === activeCategory);
  return <section data-section-id="1176" id="preguntas-frecuentes-sobre-cirugía" className="bg-background py-32 font-default">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:gap-12">
          {}
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="lg:sticky lg:top-24">
              

              <h2 className="font-default font-medium text-foreground leading-[1.02] tracking-tight text-5xl md:text-6xl lg:text-[64px] mb-8">Preguntas<br />frecuentes</h2>

              <p className="text-foreground/70 text-lg leading-relaxed max-w-md mb-6 font-default">
                Resolvemos tus dudas sobre el proceso de restauración capilar. La transparencia y la confianza son los pilares de nuestra práctica.
              </p>

              <Link className="inline-flex items-center gap-2 text-foreground font-medium border-b border-foreground/30 hover:border-primary hover:text-primary transition-colors pb-1 mb-12" href="https://​https://msinstitutocapilar.com/contacto/" newTab>
                ¿Tienes más dudas? Consúltanos
                <span aria-hidden="true">→</span>
              </Link>

              {}
              <div className="flex flex-col gap-2 max-w-[220px]">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-2">
                  FILTRAR POR TEMA
                </p>
                {categories.map((cat, i) => {
                const isActive = activeCategory === cat.id;
                return <Button key={cat.id} data-index={i} variant="ghost" onClick={() => setActiveCategory(cat.id)} className={`justify-start h-auto py-2.5 px-4 rounded-none border text-sm font-medium transition-all ${isActive ? "bg-primary text-white border-primary hover:bg-primary/90" : "bg-transparent text-foreground/70 border-border hover:bg-accent/50 hover:text-foreground hover:border-primary"}`}>
                      {cat.label}
                    </Button>;
              })}
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-7 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Accordion type="multiple" className="w-full border-t border-border">
              {filteredFaqs.map((faq, i) => {
              const questionNumber = `0${faq.id} //`;
              return <AccordionItem key={faq.id} data-index={i} value={`faq-${faq.id}`} className="border-b border-border last:border-b-0">
                    <AccordionTrigger className="hover:no-underline py-7 group [&>svg]:hidden">
                      <div className="flex items-start gap-5 md:gap-8 flex-1 text-left">
                        <span className="font-mono text-xs text-primary tracking-wider mt-2 shrink-0 w-12">
                          {questionNumber}
                        </span>
                        <span className="font-default text-foreground text-xl md:text-[22px] font-medium leading-snug flex-1">
                          {faq.question}
                        </span>
                      </div>
                      <Plus className="size-5 text-primary shrink-0 ml-4 mt-2 transition-transform duration-300 ease-out group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                    </AccordionTrigger>
                    <AccordionContent className="pb-8 pt-0">
                      <div className="flex gap-5 md:gap-8">
                        <span className="font-mono text-xs text-transparent shrink-0 w-12 hidden md:block" aria-hidden="true">
                          {questionNumber}
                        </span>
                        <div className="flex-1 max-w-[600px]">
                          <p className="text-foreground/75 text-[17px] leading-[1.7] font-default">
                            {faq.answer}
                          </p>
                          {faq.callout && <div className="mt-5 bg-accent/60 border-l-2 border-primary px-5 py-4">
                              <p className="text-foreground text-sm leading-relaxed font-medium font-default">
                                {faq.callout}
                              </p>
                            </div>}
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>

            {filteredFaqs.length === 0 && <div className="py-16 text-center">
                <p className="text-foreground/60 font-default">No hay preguntas en esta categoría.</p>
              </div>}
          </div>
        </div>
      </div>
    </section>;
}